import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import AppShell from "../layouts/AppShell";
import { GET, POST, PUT, DELETE, loading, alert, format_date, required, Select, Pagination, profile, DateInput } from "../components/CustomComponent";
import { DocumentManagerPanel } from "../components/DocumentManager";

const modalInputStyle = {
  borderRadius: 12,
  border: "1px solid #c9d7e3",
  background: "#ffffff",
  color: "#1b3950",
  boxShadow: "none",
};

const modalLabelStyle = {
  color: "#0b4f84",
  fontSize: "0.82rem",
  fontWeight: 800,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  marginBottom: 8,
};

const sectionCardStyle = {
  background: "linear-gradient(180deg, #ffffff 0%, #ebf6ff 100%)",
  border: "1px solid rgba(15, 102, 182, 0.16)",
  borderRadius: 24,
  boxShadow: "0 18px 36px rgba(11, 90, 167, 0.10)",
};

const VAT_RATE = 7; // ภาษีมูลค่าเพิ่ม %
const WHT_RATE = 3; // ภาษีหัก ณ ที่จ่าย %

// คำนวณยอดเงิน 4 ส่วน:
// - ถ้ามีใบวางบิล (doc) → ใช้ค่าจริงจากเอกสาร
// - ถ้าไม่มี → คำนวณจากยอดรวม ตาม checkbox VAT 7% / หัก ณ ที่จ่าย 3%
const computeAmounts = (formData, doc) => {
  if (doc) {
    return {
      amount: Number(doc.subtotal || 0),
      vat: Number(doc.vat_amount || 0),
      wht: Number(doc.wht_amount || 0),
      net: Number(doc.grand_total || 0),
      fromDoc: true,
    };
  }
  const base = Number(formData.amount || 0);
  const vat = formData.vat_enabled ? (base * VAT_RATE) / 100 : 0;
  const wht = formData.wht_enabled ? (base * WHT_RATE) / 100 : 0;
  return { amount: base, vat, wht, net: base + vat - wht, fromDoc: false };
};

const transactionFormDefaults = {
  uid: "",
  category_id: "",
  company_document_id: "",
  employee_id: "",
  transaction_type: "income",
  transaction_date: "",
  description: "",
  reference_no: "",
  partner_name: "",
  payment_method: "transfer",
  amount: "",
  vat_enabled: true,
  wht_enabled: false,
  vat: "",
  amount_net: "",
  status: "pending",
  note: "",
};

const transactionTypeOptions = [
  { value: "income", label: "รายรับ" },
  { value: "expense", label: "รายจ่าย" },
];

const paymentMethodOptions = [
  { value: "transfer", label: "โอนเงิน" },
  { value: "cash", label: "เงินสด" },
  { value: "credit_card", label: "บัตรเครดิต" },
  { value: "cheque", label: "เช็ค" },
];

const transactionDocTypeOptions = [
  { value: "receipt", label: "ใบเสร็จรับเงิน" },
  { value: "invoice", label: "ใบแจ้งหนี้ / ใบวางบิล" },
  { value: "payment_slip", label: "สลิปโอนเงิน" },
  { value: "contract", label: "สัญญา / ข้อตกลง" },
  { value: "other", label: "อื่นๆ" },
];

const statusOptions = [
  { value: "success", label: "สำเร็จ" },
  { value: "pending", label: "รอดำเนินการ" },
  { value: "failed", label: "ไม่สำเร็จ" },
  { value: "cancelled", label: "ยกเลิก" },
];

// คงค่าทศนิยม (สตางค์) ได้สูงสุด 2 ตำแหน่ง ใช้ระหว่างพิมพ์ในช่อง input
const sanitizeMoneyInput = (value) => {
  let v = String(value ?? "").replace(/[^\d.]/g, "");
  const dotIndex = v.indexOf(".");
  if (dotIndex !== -1) {
    v = v.slice(0, dotIndex + 1) + v.slice(dotIndex + 1).replace(/\./g, "").slice(0, 2);
  }
  return v;
};

// แสดงในช่อง input โดยใส่ตัวคั่นหลักพัน แต่ไม่บังคับทศนิยม
const formatMoneyInput = (value) => {
  if (value === undefined || value === null || value === "") {
    return "";
  }
  const str = String(value).replace(/,/g, "");
  const [intPart, decPart] = str.split(".");
  if (intPart === "" && decPart === undefined) {
    return "";
  }
  const intFmt = Number(intPart || "0").toLocaleString("en-US");
  return decPart !== undefined ? `${intFmt}.${decPart}` : intFmt;
};

// แสดงแบบอ่านอย่างเดียว (ตาราง/สรุปยอด) บังคับ 2 ตำแหน่งทศนิยม
const formatMoney = (value) => {
  const num = Number(String(value ?? "").replace(/,/g, ""));
  if (Number.isNaN(num)) {
    return "0.00";
  }
  return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const getStatusBadgeStyle = (status) => {
  if (status === "success") return { background: "rgba(47, 181, 114, 0.14)", color: "#188251" };
  if (status === "pending") return { background: "rgba(245, 166, 35, 0.16)", color: "#b97007" };
  if (status === "failed" || status === "cancelled") return { background: "rgba(229, 83, 83, 0.16)", color: "#b63636" };
  return { background: "rgba(108, 122, 137, 0.14)", color: "#566575" };
};

const getTransactionTypeLabel = (type) => {
  return transactionTypeOptions.find((item) => item.value === type)?.label || type;
};

// สร้างเอกสาร (ใบสำคัญรับ/จ่าย) ได้เฉพาะธุรกรรมที่อนุมัติแล้ว และยังไม่มีการอ้างอิงเอกสารอื่น
const canCreateDocument = (item) => item?.status === "success" && !item?.document?.uid && !(item?.reference_no || "").trim();

const buildDateValueForInput = (value) => {
  if (!value) return "";
  return value.includes("T") ? value.slice(0, 16) : value.replace(" ", "T").slice(0, 16);
};

const buildDateValueForApi = (value) => {
  if (!value) return "";
  return value.replace("T", " ").slice(0, 16);
};

const partnerTypeLabel = (type) => {
  if (type === "vendor") return "ผู้ขาย / ผู้รับจ้าง";
  if (type === "both") return "ลูกค้า / ผู้ขาย";
  return "ลูกค้า";
};

// ช่องผู้เกี่ยวข้อง: เลือกจากทะเบียนลูกค้า/คู่ค้า หรือพิมพ์ชื่อเองก็ได้
const PartnerCombobox = ({ value = "", partners = [], onChange, placeholder }) => {
  const [open, setOpen] = useState(false);

  const keyword = String(value || "").trim().toLowerCase();
  const suggestions = partners.filter((item) => {
    if (!keyword) return true;
    return `${item.name || ""} ${item.partner_code || ""}`.toLowerCase().includes(keyword);
  });

  return (
    <div style={{ position: "relative" }}>
      {open && (
        <div
          style={{ position: "fixed", inset: 0, background: "transparent", zIndex: 1000 }}
          onClick={() => setOpen(false)}
        />
      )}
      <input
        type="text"
        className="form-control"
        style={{ ...modalInputStyle, paddingRight: 34 }}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
        }}
      />
      <button
        type="button"
        className="btn btn-sm p-0 position-absolute"
        style={{ right: 10, top: "50%", transform: "translateY(-50%)", border: "none", background: "transparent", color: "#5b7d97", zIndex: 1001 }}
        title="เลือกจากทะเบียนลูกค้า / คู่ค้า"
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => setOpen((current) => !current)}
      >
        <i className="fa-solid fa-chevron-down" style={{ fontSize: "0.75rem" }} />
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 1001,
            maxHeight: 240,
            overflowY: "auto",
            padding: 6,
            borderRadius: 12,
            background: "#ffffff",
            border: "1px solid rgba(18, 125, 255, 0.16)",
            boxShadow: "0 18px 40px rgba(8, 46, 78, 0.16)",
          }}
        >
          {suggestions.length ? (
            suggestions.map((item) => (
              <button
                type="button"
                key={item.uid}
                className="btn btn-sm w-100 text-start d-flex align-items-center justify-content-between gap-2 px-2 py-2"
                style={{ border: "none", borderRadius: 8, color: "#22384a" }}
                onClick={() => {
                  onChange(item.name || "");
                  setOpen(false);
                }}
              >
                <span className="text-truncate">
                  {item.partner_code ? `${item.partner_code} · ` : ""}
                  {item.name || "-"}
                </span>
                <span className="rounded-pill px-2 py-1 flex-shrink-0 text-nowrap" style={{ background: "rgba(12, 117, 230, 0.10)", color: "#0b4f84", fontSize: "0.68rem", fontWeight: 700 }}>
                  {partnerTypeLabel(item.partner_type)}
                </span>
              </button>
            ))
          ) : (
            <div className="text-center small py-2" style={{ color: "#6b8198" }}>
              ไม่พบในทะเบียน · ใช้ชื่อที่พิมพ์ไว้ได้เลย
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const FinanceTransactionModal = ({ show, onHide, editData, onSave, categoryOptions = [], documentOptions = [], documents = [], employeeOptions = [], partners = [] }) => {
  const [formData, setFormData] = useState(transactionFormDefaults);
  const numericOnlyFields = ["amount"];

  useEffect(() => {
    setFormData({
      ...transactionFormDefaults,
      uid: editData?.uid || "",
      category_id: editData?.category_id || editData?.category?.uid || "",
      company_document_id: editData?.company_document_id || editData?.document?.uid || "",
      employee_id: editData?.employee_id || editData?.employee?.uid || "",
      transaction_type: editData?.transaction_type || "income",
      transaction_date: buildDateValueForInput(editData?.transaction_date),
      description: editData?.description || "",
      reference_no: editData?.reference_no || "",
      partner_name: editData?.partner_name || "",
      payment_method: editData?.payment_method || "transfer",
      amount: editData?.amount ?? "",
      vat_enabled: editData ? !!editData.vat_enabled : true,
      wht_enabled: editData ? !!editData.wht_enabled : false,
      vat: editData?.vat ?? "",
      amount_net: editData?.amount_net ?? "",
      status: editData?.status || "pending",
      note: editData?.note || "",
    });
  }, [editData, show]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    const normalizedValue = numericOnlyFields.includes(name) ? sanitizeMoneyInput(value) : value;

    setFormData((current) => ({
      ...current,
      [name]: normalizedValue,
    }));
  };

  const displayTitle = editData ? "แก้ไขธุรกรรม" : "เพิ่มธุรกรรมใหม่";
  const selectedDoc = documents.find((item) => item.uid === formData.company_document_id);
  const hasDoc = !!selectedDoc;
  const amounts = computeAmounts(formData, selectedDoc);

  // เลือกใบวางบิล: ผูกเอกสาร + ดึงข้อมูลจากเอกสารมาเติมให้อัตโนมัติ
  const handleSelectDocument = (uid) => {
    setFormData((current) => {
      const next = { ...current, company_document_id: uid };
      const doc = documents.find((item) => item.uid === uid);
      if (doc) {
        next.partner_name = doc.customer_name || current.partner_name;
        if (doc.subtotal !== undefined && doc.subtotal !== null) next.amount = String(doc.subtotal);
        if (!current.reference_no) next.reference_no = doc.doc_no || "";
      }
      return next;
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg" contentClassName="border-0 shadow-lg overflow-hidden" dialogClassName="modal-dialog-scrollable">
      <Modal.Header
        closeButton
        className="border-0 p-4"
        style={{
          background: "radial-gradient(circle at top right, rgba(38, 218, 222, 0.28), transparent 24%), linear-gradient(135deg, #d9eeff 0%, #bfe1ff 44%, #e7f6ff 100%)",
        }}
      >
        <div className="w-100">
          <Modal.Title className="fw-bold" style={{ color: "#11324c", fontSize: "1.75rem" }}>
            {displayTitle}
          </Modal.Title>
        </div>
      </Modal.Header>

      <Modal.Body className="px-5 py-3" style={{ background: "linear-gradient(180deg, #fcfeff 0%, #f5faff 100%)" }}>
        <div className="rounded-4 p-3 mb-4" style={{ background: "linear-gradient(135deg, rgba(12, 118, 229, 0.18) 0%, rgba(47, 213, 228, 0.20) 100%)", border: "1px solid rgba(18, 125, 255, 0.20)", boxShadow: "0 14px 28px rgba(18, 125, 255, 0.08)" }}>
          <div className="row g-3 align-items-center">
            <div className="col-md-8">
              <div className="small text-uppercase fw-bold mb-2" style={{ color: "#0f6db3", letterSpacing: "0.12em" }}>
                ข้อมูลธุรกรรม
              </div>
              <div className="fw-bold" style={{ color: "#0b3150", fontSize: "1.1rem" }}>
                {formData.reference_no || "สร้างรายการทางการเงินใหม่"}
              </div>
              <div className="small mt-1" style={{ color: "#285a7f" }}>
                {editData ? "แก้ไขข้อมูลธุรกรรมทางการเงิน" : "บันทึกรายรับหรือรายจ่ายใหม่ในระบบ"}
              </div>
            </div>
            <div className="col-md-4">
              <div className="rounded-4 p-3 d-flex align-items-center justify-content-between" style={{ background: "rgba(255,255,255,0.92)", boxShadow: "inset 0 0 0 1px rgba(18, 125, 255, 0.16)" }}>
                <div>
                  <div className="small fw-bold text-uppercase mb-1" style={{ color: "#0d6ab0", letterSpacing: "0.08em" }}>
                    สถานะ
                  </div>
                  <div className="fw-semibold" style={{ color: "#12314d" }}>
                    {statusOptions.find((item) => item.value === formData.status)?.label || "-"}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <form className="row g-4">
          <div className="col-12">
            <div className="p-4 p-md-4" style={sectionCardStyle}>
              <div className="d-flex flex-column flex-md-row align-items-md-end justify-content-between gap-2 mb-4">
                <div>
                  <div className="small text-uppercase fw-bold mb-2" style={{ color: "#0c74bc", letterSpacing: "0.12em" }}>
                    รายละเอียดธุรกรรม
                  </div>
                  <div style={{ color: "#2d6288", lineHeight: 1.7 }}>
                    กรอกข้อมูลรายรับหรือรายจ่ายพร้อมรายละเอียดการชำระเงิน
                  </div>
                </div>
              </div>
              <div className="row g-4">
                <div className="col-12">
                  <label className="form-label" style={modalLabelStyle}>อ้างอิงใบวางบิล</label>
                  <Select
                    className="form-control"
                    style={modalInputStyle}
                    value={formData.company_document_id}
                    options={documentOptions}
                    placeholder="ไม่ผูกเอกสาร"
                    onChange={(value) => handleSelectDocument(value)}
                  />
                  <div className="small mt-1" style={{ color: "#6b8198" }}>เลือกเพื่อผูกธุรกรรมกับใบวางบิล และดึงข้อมูลลูกค้า/ยอดเงินมาให้อัตโนมัติ</div>
                </div>
                <div className="col-md-6">
                  <label className="form-label" style={modalLabelStyle}>ประเภท {required}</label>
                  <Select
                    className="form-control"
                    style={modalInputStyle}
                    value={formData.transaction_type}
                    options={transactionTypeOptions}
                    placeholder="เลือกประเภท"
                    onChange={(value) => handleChange({ target: { name: "transaction_type", value } })}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label" style={modalLabelStyle}>วันที่ทำรายการ {required}</label>
                  <input
                    type="datetime-local"
                    name="transaction_date"
                    className="form-control"
                    style={modalInputStyle}
                    value={formData.transaction_date}
                    onChange={handleChange}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label" style={modalLabelStyle}>หมวดหมู่ {required}</label>
                  <Select
                    className="form-control"
                    style={modalInputStyle}
                    value={formData.category_id}
                    options={categoryOptions}
                    placeholder="เลือกหมวดหมู่"
                    onChange={(value) => handleChange({ target: { name: "category_id", value } })}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label" style={modalLabelStyle}>เลขอ้างอิง</label>
                  <input
                    type="text"
                    name="reference_no"
                    className="form-control"
                    style={modalInputStyle}
                    value={formData.reference_no}
                    onChange={handleChange}
                    placeholder="หมายเลขอ้างอิง"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label" style={modalLabelStyle}>ผู้เกี่ยวข้อง</label>
                  <PartnerCombobox
                    value={formData.partner_name}
                    partners={partners}
                    placeholder="เลือกลูกค้า / คู่ค้า หรือพิมพ์ชื่อเอง"
                    onChange={(value) => handleChange({ target: { name: "partner_name", value } })}
                  />
                  <div className="small mt-1" style={{ color: "#6b8198" }}>เลือกจากทะเบียนลูกค้า / คู่ค้า หรือพิมพ์ชื่อเองก็ได้</div>
                </div>
                <div className="col-md-6">
                  <label className="form-label" style={modalLabelStyle}>พนักงาน (ผู้เบิก)</label>
                  <Select
                    className="form-control"
                    style={modalInputStyle}
                    value={formData.employee_id}
                    options={employeeOptions}
                    placeholder="ไม่ระบุพนักงาน"
                    onChange={(value) => handleChange({ target: { name: "employee_id", value } })}
                  />
                  <div className="small mt-1" style={{ color: "#6b8198" }}>เลือกพนักงานเจ้าของรายการ เช่น ผู้เบิกค่าน้ำมัน</div>
                </div>
                <div className="col-md-6">
                  <label className="form-label" style={modalLabelStyle}>การชำระเงิน</label>
                  <Select
                    className="form-control"
                    style={modalInputStyle}
                    value={formData.payment_method}
                    options={paymentMethodOptions}
                    placeholder="เลือกวิธีชำระเงิน"
                    onChange={(value) => handleChange({ target: { name: "payment_method", value } })}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="p-4 p-md-4" style={sectionCardStyle}>
              <div className="d-flex flex-column flex-md-row align-items-md-end justify-content-between gap-2 mb-4">
                <div>
                  <div className="small text-uppercase fw-bold mb-2" style={{ color: "#0c74bc", letterSpacing: "0.12em" }}>
                    ยอดเงิน
                  </div>
                  <div style={{ color: "#2d6288", lineHeight: 1.7 }}>
                    {hasDoc ? "ดึงยอดเงินจากใบวางบิลที่อ้างอิงโดยอัตโนมัติ" : "กรอกยอดรวม แล้วเลือกภาษีที่ต้องการใช้"}
                  </div>
                </div>
              </div>
              <div className="row g-4">
                <div className="col-6 col-md-3">
                  <label className="form-label" style={modalLabelStyle}>ยอดรวม {required}</label>
                  <input
                    type="text"
                    name="amount"
                    className="form-control"
                    style={hasDoc ? { ...modalInputStyle, background: "#eef4f9", fontWeight: 700 } : modalInputStyle}
                    value={hasDoc ? formatMoney(amounts.amount) : formatMoneyInput(formData.amount)}
                    onChange={handleChange}
                    inputMode="decimal"
                    placeholder="0.00"
                    readOnly={hasDoc}
                    tabIndex={hasDoc ? -1 : undefined}
                  />
                </div>
                <div className="col-6 col-md-3">
                  <label className="form-label d-flex align-items-center gap-2" style={modalLabelStyle}>
                    {!hasDoc && (
                      <input
                        type="checkbox"
                        checked={!!formData.vat_enabled}
                        onChange={(e) => setFormData((cur) => ({ ...cur, vat_enabled: e.target.checked, wht_enabled: e.target.checked ? false : cur.wht_enabled }))}
                      />
                    )}
                    <span>VAT {VAT_RATE}%</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    style={{ ...modalInputStyle, background: "#eef4f9", color: "#0b3150", fontWeight: 700 }}
                    value={formatMoney(amounts.vat)}
                    readOnly
                    tabIndex={-1}
                    placeholder="0.00"
                  />
                </div>
                <div className="col-6 col-md-3">
                  <label className="form-label d-flex align-items-center gap-2" style={modalLabelStyle}>
                    {!hasDoc && (
                      <input
                        type="checkbox"
                        checked={!!formData.wht_enabled}
                        onChange={(e) => setFormData((cur) => ({ ...cur, wht_enabled: e.target.checked, vat_enabled: e.target.checked ? false : cur.vat_enabled }))}
                      />
                    )}
                    <span>หัก ณ ที่จ่าย {WHT_RATE}%</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    style={{ ...modalInputStyle, background: "#fdeef0", color: "#9b2c3a", fontWeight: 700 }}
                    value={formatMoney(amounts.wht)}
                    readOnly
                    tabIndex={-1}
                    placeholder="0.00"
                  />
                </div>
                <div className="col-6 col-md-3">
                  <label className="form-label" style={modalLabelStyle}>ยอดสุทธิ (อัตโนมัติ)</label>
                  <input
                    type="text"
                    name="amount_net"
                    className="form-control"
                    style={{ ...modalInputStyle, background: "#e7f6ee", color: "#0b6b3a", fontWeight: 800 }}
                    value={formatMoney(amounts.net)}
                    readOnly
                    tabIndex={-1}
                    placeholder="0.00"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label" style={modalLabelStyle}>คำอธิบาย</label>
                  <textarea
                    name="description"
                    className="form-control"
                    style={{ ...modalInputStyle, minHeight: 108, resize: "none" }}
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="รายละเอียดธุรกรรม"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label" style={modalLabelStyle}>หมายเหตุ</label>
                  <textarea
                    name="note"
                    className="form-control"
                    style={{ ...modalInputStyle, minHeight: 108, resize: "none" }}
                    value={formData.note}
                    onChange={handleChange}
                    placeholder="ความคิดเห็นหรือข้อมูลเพิ่มเติม"
                  />
                </div>
              </div>
            </div>
          </div>

          {editData?.uid && (
          <div className="col-12">
            <div className="p-4 p-md-4" style={sectionCardStyle}>
              <div className="d-flex flex-column flex-md-row align-items-md-end justify-content-between gap-2 mb-4">
                <div>
                  <div className="small text-uppercase fw-bold mb-2" style={{ color: "#0c74bc", letterSpacing: "0.12em" }}>
                    เอกสารแนบ
                  </div>
                  <div style={{ color: "#2d6288", lineHeight: 1.7 }}>
                    แนบไฟล์ประกอบธุรกรรม เช่น ใบเสร็จ ใบแจ้งหนี้ หรือสลิปโอนเงิน ได้หลายไฟล์
                  </div>
                </div>
              </div>
              <DocumentManagerPanel ownerType="finance_transaction" ownerUid={editData.uid} docTypeOptions={transactionDocTypeOptions} />
            </div>
          </div>
          )}
        </form>
      </Modal.Body>

      <Modal.Footer className="border-0 px-4 px-md-5 py-4" style={{ background: "linear-gradient(180deg, rgba(243,249,255,0.84) 0%, rgba(248,252,255,0.98) 100%)" }}>
        <div className="d-flex flex-column flex-sm-row gap-3 w-100 justify-content-end">
          <button
            type="button"
            className="btn px-4"
            onClick={onHide}
            style={{ borderRadius: 16, background: "#ffffff", border: "1px solid rgba(18, 125, 255, 0.12)", color: "#4f6d83", fontWeight: 600 }}
          >
            ยกเลิก
          </button>
          <button
            type="button"
            className="btn px-4 text-white"
            onClick={() => onSave(formData)}
            style={{ borderRadius: 16, border: "none", background: "linear-gradient(135deg, #0c75e6 0%, #2fd5e4 100%)", boxShadow: "0 16px 28px rgba(12, 117, 230, 0.24)", fontWeight: 700 }}
          >
            บันทึกข้อมูล
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

const PAGE_SIZE = 20;

const getPaymentMethodLabel = (method) => paymentMethodOptions.find((item) => item.value === method)?.label || method || "-";

const getEmployeeName = (employee) => {
  if (!employee) return "";
  const fullName = `${employee.first_name || ""} ${employee.last_name || ""}`.trim();
  return fullName || employee.nickname || employee.employee_code || "";
};

const defaultSummary = { total_count: 0, total_income: 0, total_income_net: 0, total_expense: 0, total_expense_net: 0, balance: 0, total_subtotal: 0, total_vat: 0, total_wht: 0, total_net: 0 };
const defaultFilters = { searchText: "", date_from: "", date_to: "" };

// แท็บแยกประเภทธุรกรรม ("" = ทั้งหมด)
const typeTabs = [
  { value: "", label: "ทั้งหมด", icon: "fa-layer-group", color: "#0c76e5" },
  { value: "income", label: "รายรับ", icon: "fa-arrow-trend-up", color: "#188251" },
  { value: "expense", label: "รายจ่าย", icon: "fa-arrow-trend-down", color: "#b63636" },
];

export default function FinanceTransactions() {
  const title = "ธุรกรรมการเงิน";
  const description = "จัดการรายการรายรับและรายจ่าย รวมถึงสร้างและแก้ไขธุรกรรมทางการเงิน";
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [data, setData] = useState([]);
  const [financeCategories, setFinanceCategories] = useState([]);
  const [billingDocuments, setBillingDocuments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [partners, setPartners] = useState([]);
  const [summary, setSummary] = useState(defaultSummary);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);

  const [searchInput, setSearchInput] = useState("");
  const [dateFromInput, setDateFromInput] = useState("");
  const [dateToInput, setDateToInput] = useState("");
  const [filters, setFilters] = useState(defaultFilters);
  const [typeTab, setTypeTab] = useState("");

  const [actionMenu, setActionMenu] = useState(null); // เมนู "..." ของแต่ละแถว { x, y, item }
  const [detailTarget, setDetailTarget] = useState(null); // ธุรกรรมที่กดดูข้อมูลทั้งหมด

  const [showReject, setShowReject] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");

  const [docPickerTarget, setDocPickerTarget] = useState(null);
  const navigate = useNavigate();

  const handleOpenModal = (item = null) => {
    setEditData(item);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setEditData(null);
    setShowModal(false);
  };

  const buildFilterBody = useCallback(
    () => ({
      data_search: filters.searchText.trim() || "",
      date_from: filters.date_from || "",
      date_to: filters.date_to || "",
    }),
    [filters]
  );

  const getData = useCallback(async () => {
    loading(true);
    const result = await GET("v1/admin/finance-transactions", {
      page,
      page_size: PAGE_SIZE,
      ...buildFilterBody(),
      // แท็บประเภทกรองเฉพาะตาราง การ์ดสรุปด้านบนยังรวมทุกประเภทตามช่วงวันที่/คำค้น
      transaction_type: typeTab,
    });
    if (result?.status) {
      setData(result.data || []);
      const count = result?.pagination?.count || result?.data?.length || 0;
      setPageCount(Math.max(1, Math.ceil(count / PAGE_SIZE)));
    }
    loading(false);
  }, [page, typeTab, buildFilterBody]);

  const getSummary = useCallback(async () => {
    const result = await GET("v1/admin/finance-transactions/summary", buildFilterBody());
    if (result?.status) {
      setSummary({ ...defaultSummary, ...(result.data || {}) });
    }
  }, [buildFilterBody]);

  const getFinanceCategories = async () => {
    loading(true);
    const result = await GET("v1/admin/finance-categories", {
      page: 1,
      page_size: 100,
      data_search: "",
      transaction_type: "",
    });
    if (result?.status) {
      setFinanceCategories(result.data || []);
    }
    loading(false);
  };

  const getBillingDocuments = async () => {
    const result = await GET("v1/admin/company-documents", { page: 0, doc_type: "billing_note" });
    if (result?.status) {
      setBillingDocuments(result.data || []);
    }
  };

  const getEmployees = async () => {
    const result = await GET("v1/admin/employees", { page: 0, data_search: "" });
    if (result?.status) {
      setEmployees(result.data || []);
    }
  };

  // ทะเบียนลูกค้า/คู่ค้า สำหรับเลือกในช่องผู้เกี่ยวข้อง
  const getPartners = async () => {
    const result = await GET("v1/admin/partners", { page: 0, is_active: true });
    if (result?.status) {
      setPartners(result.data || []);
    }
  };

  useEffect(() => {
    getFinanceCategories();
    getBillingDocuments();
    getEmployees();
    getPartners();
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    getSummary();
  }, [getSummary]);

  // ปิดเมนู "..." เมื่อคลิกที่อื่น เลื่อนจอ หรือกด Esc
  useEffect(() => {
    if (!actionMenu) return undefined;

    const close = () => setActionMenu(null);
    const onKeyDown = (event) => event.key === "Escape" && setActionMenu(null);

    window.addEventListener("click", close);
    window.addEventListener("scroll", close, true);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [actionMenu]);

  const handleSearch = () => {
    setPage(1);
    setFilters({
      searchText: searchInput,
      date_from: dateFromInput,
      date_to: dateToInput,
    });
  };

  const handleResetFilters = () => {
    setSearchInput("");
    setDateFromInput("");
    setDateToInput("");
    setPage(1);
    setFilters(defaultFilters);
  };

  const handleChangeTypeTab = (value) => {
    setPage(1);
    setTypeTab(value);
  };

  // เปิดเมนู "..." ตรงตำแหน่งที่กด (position: fixed จึงไม่โดนตารางที่ scroll ได้บังไว้)
  const openActionMenu = (event, item) => {
    event.preventDefault();
    event.stopPropagation();
    const menuWidth = 220;
    const menuHeight = 260;
    setActionMenu({
      x: Math.min(event.clientX, window.innerWidth - menuWidth),
      y: Math.min(event.clientY, window.innerHeight - menuHeight),
      item,
    });
  };

  const refresh = () => {
    getData();
    getSummary();
  };

  const categoryOptions = financeCategories.map((item) => ({
    value: item.uid,
    label: item.category_name || item.category?.category_name || item.category_code || "-",
  }));

  const documentOptions = billingDocuments.map((item) => ({
    value: item.uid,
    label: `${item.doc_no || "-"}${item.customer_name ? ` · ${item.customer_name}` : ""}`,
  }));

  const employeeOptions = employees.map((item) => {
    const fullName = `${item.first_name || ""} ${item.last_name || ""}`.trim();
    const label = fullName || item.nickname || item.employee_code || "-";
    return {
      value: item.uid,
      label: item.employee_code ? `${item.employee_code} · ${label}` : label,
    };
  });

  // อนุมัติ/ปฏิเสธได้เฉพาะผู้ใช้ตำแหน่ง CEO
  const isCEO = (profile?.employee?.position || "").toUpperCase() === "CEO";

  const summaryCards = [
    { label: "รวมรายรับ", value: `${formatMoney(summary.total_income_net)} `, color: "#1f9d57" },
    { label: "รวมรายจ่าย", value: `${formatMoney(summary.total_expense_net)} `, color: "#e54848" },
    { label: `ภาษีมูลค่าเพิ่ม ${VAT_RATE}%`, value: `${formatMoney(summary.total_vat)} `, color: "#0b4f84" },
    { label: `ภาษีหัก ณ ที่จ่าย ${WHT_RATE}%`, value: `${formatMoney(summary.total_wht)} `, color: "#b63636" },
    { label: "คงเหลือ", value: `${formatMoney(Number(summary.balance) || 0)} `, color: "#0c76e5" },
  ];

  const handleDelete = async (item) => {
    if (!(await alert("", "warning", "ลบธุรกรรม", `ต้องการลบธุรกรรม ${item.transaction_code || item.reference_no || ""} ?`, "ลบ", "ยกเลิก"))) {
      return;
    }
    loading(true);
    const result = await DELETE(`v1/admin/finance-transactions/${item.uid}`);
    if (result?.status === true) {
      alert("", "success", "สำเร็จ", "ลบธุรกรรมเรียบร้อยแล้ว");
      refresh();
    } else {
      alert("", "warning", "แจ้งเตือน", result?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่");
    }
    loading(false);
  };

  const handleApprove = async (item) => {
    if (!(await alert("", "info", "อนุมัติธุรกรรม", "ยืนยันการอนุมัติธุรกรรมนี้?", "อนุมัติ", "ยกเลิก"))) {
      return;
    }
    loading(true);
    const result = await PUT(`v1/admin/finance-transactions/${item.uid}/approve`, {});
    if (result?.status === true) {
      alert("", "success", "สำเร็จ", "อนุมัติธุรกรรมเรียบร้อยแล้ว");
      refresh();
    } else {
      alert("", "warning", "แจ้งเตือน", result?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่");
    }
    loading(false);
  };

  // ไปหน้าเอกสารการเงิน พร้อมข้อมูลตั้งต้นจากธุรกรรมที่เลือก
  const handleCreateDocument = (docType) => {
    const item = docPickerTarget;
    if (!item) return;
    const docDate = (item.transaction_date || "").slice(0, 10) || undefined;
    navigate("/company-documents", {
      state: {
        createDoc: {
          doc_type: docType,
          doc_date: docDate,
          customer_name: item.partner_name || getEmployeeName(item.employee) || "",
          purpose: item.description || "",
          note: item.note || "",
          wht_amount: Number(item.wht) || 0,
          items: [
            {
              doc_date: docDate,
              description: item.description || "",
              amount: Number(item.amount_net || item.amount) || 0,
            },
          ],
          // ใช้ผูกเลขที่เอกสารที่สร้างเสร็จกลับมาเป็นเลขอ้างอิงของธุรกรรมนี้
          transactionUid: item.uid,
        },
      },
    });
    setDocPickerTarget(null);
  };

  const openReject = (item) => {
    setRejectTarget(item);
    setRejectReason("");
    setShowReject(true);
  };

  const submitReject = async () => {
    if (!rejectTarget) return;
    loading(true);
    const result = await PUT(`v1/admin/finance-transactions/${rejectTarget.uid}/reject`, { rejection_reason: rejectReason.trim() });
    if (result?.status === true) {
      setShowReject(false);
      setRejectTarget(null);
      alert("", "success", "สำเร็จ", "ปฏิเสธธุรกรรมเรียบร้อยแล้ว");
      refresh();
    } else {
      alert("", "warning", "แจ้งเตือน", result?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่");
    }
    loading(false);
  };

  const CreateUpdate = async (formData) => {
    const requiredFields = [
      { key: "category_id", label: "หมวดหมู่" },
      { key: "transaction_type", label: "ประเภท" },
      { key: "transaction_date", label: "วันที่ทำรายการ" },
      { key: "amount", label: "จำนวนเงิน" },
    ];

    const missingField = requiredFields.find(({ key }) => {
      const value = formData?.[key];
      return value === undefined || value === null || String(value).trim() === "";
    });

    if (missingField) {
      await alert("", "warning", "แจ้งเตือน", `กรุณากรอกข้อมูล ${missingField.label}`);
      return;
    }

    if (
      await alert("", "info", "ยืนยัน", "ยืนยันการบันทึกข้อมูลหรือไม่?", "ยืนยัน", "ยกเลิก")
    ) {
      loading(true);
      const uid = formData?.uid || editData?.uid;
      const linkedDoc = billingDocuments.find((item) => item.uid === formData.company_document_id);
      const amounts = computeAmounts(formData, linkedDoc);
      const body = {
        category_id: formData.category_id,
        company_document_id: formData.company_document_id || null,
        employee_id: formData.employee_id || null,
        transaction_type: formData.transaction_type,
        transaction_date: buildDateValueForApi(formData.transaction_date),
        description: formData.description,
        reference_no: formData.reference_no,
        partner_name: formData.partner_name,
        payment_method: formData.payment_method,
        amount: amounts.amount,
        vat_enabled: linkedDoc ? !!linkedDoc.vat_enabled : !!formData.vat_enabled,
        vat: amounts.vat,
        wht_enabled: linkedDoc ? !!linkedDoc.wht_enabled : !!formData.wht_enabled,
        wht: amounts.wht,
        amount_net: amounts.net,
        status: formData.status,
        note: formData.note,
      };

      const result = !uid
        ? await POST("v1/admin/finance-transactions", body)
        : await PUT(`v1/admin/finance-transactions/${uid}`, body);

      if (result?.status === true) {
        alert("", "success", "สำเร็จ", "ทำรายการสำเร็จแล้ว");
        refresh();
        handleCloseModal();
      } else {
        alert("", "warning", "แจ้งเตือน", result?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
      }
      loading(false);
    }
  };

  // รวมทุกคำสั่งของแถวไว้ในเมนู "..." เมนูเดียว
  const actionMenuItems = actionMenu
    ? [
        ...(actionMenu.item.status === "pending" && isCEO
          ? [
              { label: "อนุมัติ", icon: "fa-check", color: "#188251", onClick: () => handleApprove(actionMenu.item) },
              { label: "ปฏิเสธ", icon: "fa-xmark", danger: true, onClick: () => openReject(actionMenu.item) },
            ]
          : []),
        ...(canCreateDocument(actionMenu.item)
          ? [{ label: "สร้างเอกสาร", icon: "fa-file-circle-plus", color: "#0c76e5", onClick: () => setDocPickerTarget(actionMenu.item) }]
          : []),
        { label: "ดูข้อมูล", icon: "fa-circle-info", onClick: () => setDetailTarget(actionMenu.item) },
        { label: "แก้ไข", icon: "fa-pen", onClick: () => handleOpenModal(actionMenu.item) },
        { divider: true },
        { label: "ลบ", icon: "fa-trash", danger: true, onClick: () => handleDelete(actionMenu.item) },
      ]
    : [];

  return (
    <AppShell title={title} description={description}>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-start mb-4 gap-3">
        <div className="d-flex gap-2 flex-wrap align-items-center filters-mobile-stack">
          <input
            type="text"
            className="form-control"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyDown={(event) => event.key === "Enter" && handleSearch()}
            placeholder="ค้นหาธุรกรรม..."
            style={{ width: "220px", borderRadius: "12px" }}
          />
          <DateInput
            className="form-control"
            value={dateFromInput}
            onChange={(event) => setDateFromInput(event.target.value)}
            title="ตั้งแต่วันที่"
            style={{ width: "160px", borderRadius: "12px" }}
          />
          <span className="text-secondary">ถึง</span>
          <DateInput
            className="form-control"
            value={dateToInput}
            onChange={(event) => setDateToInput(event.target.value)}
            title="ถึงวันที่"
            style={{ width: "160px", borderRadius: "12px" }}
          />
          <button type="button" className="btn btn-primary px-4" onClick={handleSearch} style={{ borderRadius: "12px" }}>
            ค้นหา
          </button>
          <button
            type="button"
            className="btn btn-light px-4"
            onClick={handleResetFilters}
            style={{ borderRadius: "12px", border: "1px solid rgba(18, 125, 255, 0.14)", color: "#4f6d83" }}
          >
            ล้างค่า
          </button>
        </div>
        <button onClick={() => handleOpenModal()} className="btn btn-primary px-4 flex-shrink-0" style={{ borderRadius: "12px" }}>
          + เพิ่มธุรกรรม
        </button>
      </div>

      <div className="row g-3 mb-4">
        {summaryCards.map((item) => (
          <div key={item.label} className="col-12 col-sm-6 col-md-4 col-xl">
            <div className="fintx-kpi-card h-100 rounded-4 p-4 d-flex flex-column justify-content-center" style={{ background: "linear-gradient(180deg, #ffffff 0%, #f2f9ff 100%)", border: "1px solid rgba(18, 125, 255, 0.10)", boxShadow: "0 14px 28px rgba(12, 118, 229, 0.06)" }}>
              <p className="small text-uppercase text-secondary mb-1">{item.label}</p>
              <h3 className="fintx-kpi-value h4 mb-0 fw-bold" style={{ color: item.color }}>
                {item.value}
              </h3>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-4 p-3 bg-white" style={{ border: "1px solid rgba(18, 125, 255, 0.10)", boxShadow: "0 4px 12px rgba(12, 118, 229, 0.04)" }}>
        <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-3 mb-4">
          <h5 className="mb-0 fw-semibold text-dark">รายการธุรกรรม</h5>
          <div className="d-inline-flex gap-2 p-1 flex-wrap" style={{ background: "rgba(12, 118, 229, 0.06)", borderRadius: 14 }}>
            {typeTabs.map((tab) => {
              const isActive = typeTab === tab.value;
              return (
                <button
                  key={tab.value || "all"}
                  type="button"
                  onClick={() => handleChangeTypeTab(tab.value)}
                  className="btn btn-sm px-3 py-2 fw-semibold text-nowrap"
                  style={{
                    borderRadius: 12,
                    border: "none",
                    background: isActive ? "#ffffff" : "transparent",
                    color: isActive ? tab.color : "#6e8c9c",
                    boxShadow: isActive ? `inset 0 0 0 2px ${tab.color}, 0 6px 14px rgba(12, 118, 229, 0.10)` : "none",
                  }}
                >
                  <i className={`fa-solid ${tab.icon} me-2`} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
        <div className="table-responsive table-scroll-mobile">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th className="text-secondary fw-medium rounded-start">รหัส</th>
                <th className="text-secondary fw-medium">เลขอ้างอิง</th>
                <th className="text-secondary fw-medium">วันที่</th>
                <th className="text-secondary fw-medium text-center text-nowrap">ประเภท</th>
                <th className="text-secondary fw-medium">คำอธิบาย</th>
                <th className="text-secondary fw-medium text-nowrap">พนักงาน</th>
                <th className="text-secondary fw-medium text-end text-nowrap">ยอดสุทธิ</th>
                <th className="text-secondary fw-medium text-center rounded-end">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              {data.length > 0 ? (
                data.map((item) => {
                  const isIncome = item.transaction_type === "income";
                  const isPending = item.status === "pending";
                  const descriptionText = item.description || "";
                  return (
                    <tr key={item.uid}>
                      <td
                        data-label="รหัส"
                        className="fw-semibold"
                        style={{ color: "#0b4f84", boxShadow: isPending ? "inset 3px 0 0 #f5a623" : undefined }}
                      >
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <span>{item.transaction_code || "-"}</span>
                          {/* เอาคอลัมน์สถานะออกแล้ว จึงติดป้ายเฉพาะรายการที่ยังรออนุมัติไว้ให้เห็น */}
                          {isPending ? (
                            <span
                              className="rounded-pill px-2 py-1 d-inline-flex align-items-center text-nowrap"
                              style={{ background: "rgba(245, 166, 35, 0.16)", color: "#b97007", fontSize: "0.68rem", fontWeight: 700 }}
                              title="รายการนี้ยังรออนุมัติ"
                            >
                              <i className="fa-solid fa-clock me-1" />
                              รออนุมัติ
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td data-label="เลขอ้างอิง">
                        {item.reference_no ? (
                          <span className="rounded-pill py-1 small fw-semibold" style={{ color: "#0f6bdc" }}>
                            {item.reference_no}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td data-label="วันที่">{format_date(item.transaction_date, "dd/mm/yyyy hh:mm")}</td>
                      <td data-label="ประเภท" className="text-center text-nowrap">
                        <span className="rounded-pill px-3 py-1 small fw-semibold text-nowrap d-inline-block" style={isIncome ? { background: "rgba(47, 181, 114, 0.14)", color: "#188251" } : { background: "rgba(229, 83, 83, 0.14)", color: "#b63636" }}>
                          {getTransactionTypeLabel(item.transaction_type)}
                        </span>
                      </td>
                      <td data-label="คำอธิบาย">
                        {descriptionText ? (
                          <button
                            type="button"
                            className="btn btn-link p-0 border-0 text-start text-decoration-none align-top"
                            title="กดเพื่อดูข้อมูลทั้งหมด"
                            onClick={() => setDetailTarget(item)}
                            style={{ maxWidth: 280, color: "#22384a" }}
                          >
                            <span className="d-block text-truncate" style={{ maxWidth: 280 }}>
                              {descriptionText}
                            </span>
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td data-label="พนักงาน (ผู้เบิก)" className="text-nowrap">{item.employee?.nickname || getEmployeeName(item.employee) || "-"}</td>
                      <td data-label="ยอดสุทธิ" className="text-end fw-bold" style={{ color: isIncome ? "#188251" : "#b63636" }}>
                        {/* ยอดสุทธิหลังบวก VAT และหัก ณ ที่จ่ายแล้ว */}
                        {isIncome ? "+" : "-"}{formatMoney(item.amount_net)} 
                      </td>
                      <td data-label="จัดการ" className="text-center">
                        <button
                          type="button"
                          onClick={(event) => openActionMenu(event, item)}
                          className="btn btn-sm btn-outline-secondary rounded-3"
                          title="เมนูจัดการ"
                        >
                          <i className="fa-solid fa-ellipsis" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="text-center py-5 text-secondary">
                    ไม่พบข้อมูลธุรกรรม
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {pageCount > 1 && (
          <div className="d-flex justify-content-end mt-3">
            <Pagination count={pageCount} page={page} onChange={(event, value) => setPage(value)} color="primary" shape="rounded" />
          </div>
        )}
      </div>

      <FinanceTransactionModal
        show={showModal}
        onHide={handleCloseModal}
        editData={editData}
        onSave={CreateUpdate}
        categoryOptions={categoryOptions}
        documentOptions={documentOptions}
        documents={billingDocuments}
        employeeOptions={employeeOptions}
        partners={partners}
      />

      <Modal show={!!docPickerTarget} onHide={() => setDocPickerTarget(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ color: "#0b4f84", fontSize: "1.25rem", fontWeight: 700 }}>สร้างเอกสารจากธุรกรรม</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-secondary mb-3">
            เลือกประเภทเอกสารที่ต้องการสร้างจากธุรกรรม{" "}
            <span className="fw-semibold text-dark">{docPickerTarget?.transaction_code || docPickerTarget?.reference_no || ""}</span>
            {" "}ระบบจะดึงข้อมูลคู่ค้า/รายละเอียด/ยอดเงินไปตั้งต้นในหน้าเอกสารให้
          </p>
          <div className="d-flex flex-column flex-sm-row gap-3">
            <button
              type="button"
              className="btn w-100 flex-sm-fill py-3"
              style={{ borderRadius: 14, border: "1px solid rgba(220,38,38,0.3)", background: "rgba(220,38,38,0.06)", color: "#dc2626", fontWeight: 700 }}
              onClick={() => handleCreateDocument("payment_voucher")}
            >
              <i className="fa-solid fa-money-check-dollar d-block mb-2" style={{ fontSize: 22 }} />
              ใบสำคัญจ่าย
            </button>
            <button
              type="button"
              className="btn w-100 flex-sm-fill py-3"
              style={{ borderRadius: 14, border: "1px solid rgba(14,165,233,0.3)", background: "rgba(14,165,233,0.06)", color: "#0ea5e9", fontWeight: 700 }}
              onClick={() => handleCreateDocument("receipt_voucher")}
            >
              <i className="fa-solid fa-hand-holding-dollar d-block mb-2" style={{ fontSize: 22 }} />
              ใบสำคัญรับ
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={showReject} onHide={() => setShowReject(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ color: "#b63636", fontSize: "1.25rem", fontWeight: 700 }}>ปฏิเสธธุรกรรม</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-secondary mb-2">
            ธุรกรรม: <span className="fw-semibold text-dark">{rejectTarget?.transaction_code || rejectTarget?.reference_no || "-"}</span>
          </p>
          <label className="form-label" style={modalLabelStyle}>เหตุผลการปฏิเสธ</label>
          <textarea
            className="form-control"
            style={{ ...modalInputStyle, minHeight: 100, resize: "none" }}
            value={rejectReason}
            onChange={(event) => setRejectReason(event.target.value)}
            placeholder="ระบุเหตุผล (ถ้ามี)"
          />
        </Modal.Body>
        <Modal.Footer className="border-0">
          <button type="button" className="btn btn-light px-4" onClick={() => setShowReject(false)} style={{ borderRadius: 12 }}>
            ยกเลิก
          </button>
          <button type="button" className="btn btn-danger px-4" onClick={submitReject} style={{ borderRadius: 12 }}>
            ยืนยันปฏิเสธ
          </button>
        </Modal.Footer>
      </Modal>

      {/* ข้อมูลธุรกรรมทั้งหมด */}
      <Modal show={!!detailTarget} onHide={() => setDetailTarget(null)} centered size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title style={{ color: "#0b4f84", fontSize: "1.25rem", fontWeight: 700 }}>
            <i className="fa-solid fa-circle-info me-2" />
            ข้อมูลธุรกรรม
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailTarget ? (
            <>
              <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
                <span className="fw-bold" style={{ color: "#0b4f84", fontSize: "1.05rem" }}>
                  {detailTarget.transaction_code || "-"}
                </span>
                <span
                  className="rounded-pill px-3 py-1 small fw-semibold text-nowrap"
                  style={
                    detailTarget.transaction_type === "income"
                      ? { background: "rgba(47, 181, 114, 0.14)", color: "#188251" }
                      : { background: "rgba(229, 83, 83, 0.14)", color: "#b63636" }
                  }
                >
                  {getTransactionTypeLabel(detailTarget.transaction_type)}
                </span>
                <span className="rounded-pill px-3 py-1 small fw-semibold text-nowrap" style={getStatusBadgeStyle(detailTarget.status)}>
                  {statusOptions.find((item) => item.value === detailTarget.status)?.label || detailTarget.status || "-"}
                </span>
              </div>

              {/* ยอดเงินทั้งชุด */}
              <div className="rounded-4 p-3 mb-3" style={{ background: "linear-gradient(180deg, #ffffff 0%, #eef7ff 100%)", border: "1px solid rgba(18, 125, 255, 0.14)" }}>
                <div className="row g-3 text-center text-md-start">
                  {[
                    { label: "ยอดรวม (ก่อนภาษี)", value: formatMoney(detailTarget.amount), color: "#12314d" },
                    { label: `VAT ${VAT_RATE}%`, value: formatMoney(detailTarget.vat), color: "#0b4f84" },
                    { label: `หัก ณ ที่จ่าย ${WHT_RATE}%`, value: `-${formatMoney(detailTarget.wht)}`, color: "#b63636" },
                    {
                      label: "ยอดสุทธิ",
                      value: formatMoney(detailTarget.amount_net),
                      color: detailTarget.transaction_type === "income" ? "#188251" : "#b63636",
                      strong: true,
                    },
                  ].map((box) => (
                    <div key={box.label} className="col-6 col-md-3">
                      <div className="small mb-1" style={{ color: "#7d94a6", fontSize: "0.72rem" }}>{box.label}</div>
                      <div className="fw-bold" style={{ color: box.color, fontSize: box.strong ? "1.15rem" : "1rem", fontVariantNumeric: "tabular-nums" }}>
                        {box.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ข้อมูลรายการ */}
              <div className="row g-3">
                {[
                  { label: "วันที่ทำรายการ", value: detailTarget.transaction_date ? format_date(detailTarget.transaction_date, "dd/mm/yyyy hh:mm") : "-" },
                  { label: "หมวดหมู่", value: detailTarget.category?.category_name || "-" },
                  { label: "ผู้เกี่ยวข้อง", value: detailTarget.partner_name || "-" },
                  { label: "พนักงาน (ผู้เบิก)", value: getEmployeeName(detailTarget.employee) || "-" },
                  { label: "วิธีชำระเงิน", value: getPaymentMethodLabel(detailTarget.payment_method) },
                  { label: "เลขอ้างอิง", value: detailTarget.reference_no || "-" },
                  { label: "เอกสารอ้างอิง", value: detailTarget.document?.doc_no || "-" },
                  { label: "บันทึกเมื่อ", value: detailTarget.created_at ? format_date(detailTarget.created_at, "dd/mm/yyyy hh:mm") : "-" },
                ].map((field) => (
                  <div key={field.label} className="col-md-6">
                    <div className="small mb-1" style={{ color: "#7d94a6", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.04em" }}>
                      {field.label}
                    </div>
                    <div style={{ color: "#22384a", wordBreak: "break-word" }}>{field.value}</div>
                  </div>
                ))}
              </div>

              <hr style={{ borderColor: "rgba(18, 125, 255, 0.14)" }} />

              <div className="mb-3">
                <div className="small mb-1" style={{ color: "#7d94a6", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.04em" }}>คำอธิบาย</div>
                <div style={{ color: "#22384a", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{detailTarget.description || "-"}</div>
              </div>

              <div className="mb-3">
                <div className="small mb-1" style={{ color: "#7d94a6", fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.04em" }}>หมายเหตุ</div>
                <div style={{ color: "#22384a", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{detailTarget.note || "-"}</div>
              </div>

              {detailTarget.approved_at ? (
                <div className="rounded-3 p-3 small" style={{ background: "rgba(47, 181, 114, 0.10)", color: "#188251" }}>
                  <i className="fa-solid fa-circle-check me-2" />
                  อนุมัติเมื่อ {format_date(detailTarget.approved_at, "dd/mm/yyyy hh:mm")}
                </div>
              ) : null}

              {detailTarget.rejected_at ? (
                <div className="rounded-3 p-3 small" style={{ background: "rgba(229, 83, 83, 0.10)", color: "#b63636" }}>
                  <i className="fa-solid fa-circle-xmark me-2" />
                  ปฏิเสธเมื่อ {format_date(detailTarget.rejected_at, "dd/mm/yyyy hh:mm")}
                  {detailTarget.rejection_reason ? ` · เหตุผล: ${detailTarget.rejection_reason}` : ""}
                </div>
              ) : null}
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer className="border-0">
          <button type="button" className="btn btn-light px-4" onClick={() => setDetailTarget(null)} style={{ borderRadius: 12 }}>
            ปิด
          </button>
          <button
            type="button"
            className="btn btn-primary px-4"
            style={{ borderRadius: 12 }}
            onClick={() => {
              const item = detailTarget;
              setDetailTarget(null);
              handleOpenModal(item);
            }}
          >
            <i className="fa-solid fa-pen me-2" />
            แก้ไข
          </button>
        </Modal.Footer>
      </Modal>

      {/* เมนู "..." ของแถวในตาราง */}
      {actionMenu && (
        <div className="drive-context-menu" style={{ top: actionMenu.y, left: actionMenu.x }} onClick={(event) => event.stopPropagation()}>
          {actionMenuItems.map((action, index) =>
            action.divider ? (
              <div className="drive-context-divider" key={`divider-${index}`} />
            ) : (
              <button
                type="button"
                key={action.label}
                className={action.danger ? "is-danger" : ""}
                style={action.color ? { color: action.color, fontWeight: 600 } : undefined}
                onClick={() => {
                  setActionMenu(null);
                  action.onClick();
                }}
              >
                <i className={`fa-solid ${action.icon}`} style={{ width: 16 }} />
                {action.label}
              </button>
            )
          )}
        </div>
      )}
    </AppShell>
  );
}
